import { getFirmById, getOfficesByFirmId } from "../Services/Firm";
import {
  getFirmContactByFirmId,
  getFirmContactByOfficeId,
} from "../Services/FirmContact";
import { getOfficeById } from "../Services/Office";
import type { FirmContactView } from "../interfaces/models/IFirmContact";
import { getCurrentFiscalYear } from "./dates";
import { resolveFirmJurisdictionDisplay } from "./resolveFirmJurisdictionDisplay";
import { buildAccountingFirmOfficeGroups } from "./accountingOfficeTableGrouping";
import { ServiceType as FirmServiceTypeLabels } from "../Enums/FirmEnum";
import { MembershipType, ServiceType as OfficeServiceTypeLabels } from "../Enums/OfficeEnum";
import {
  formatOfficeBlockHeading,
} from "../pages/ManageFirm/Professionals/manageDataAdminOfficeRules";
import type IFirm from "../interfaces/models/IFirm";
import type { IOfficeNew } from "../interfaces/models/IOffice";

export type AccountingOfficeDetail = Omit<IOfficeNew, "serviceType"> & {
  id: string;
  serviceType?: IOfficeNew["serviceType"] | IFirm["serviceType"];
};

type FirmAccountingSource = Pick<
  IFirm,
  | "shortId"
  | "dateJoined"
  | "dateWithdrawn"
  | "billingStartDate"
  | "membershipStatus"
  | "firmProfessionalsInPrimary"
  | "professionalsInPrimary"
  | "membershipType"
  | "serviceType"
  | "jurisdiction"
  | "jurisdictionNames"
>;

export function resolveAccountingServiceType(
  officeServiceType?: IOfficeNew["serviceType"],
  firmServiceType?: IFirm["serviceType"],
): AccountingOfficeDetail["serviceType"] {
  const officeKey = String(officeServiceType ?? "").trim();
  if (officeKey && officeKey !== "other") {
    return officeServiceType;
  }
  const firmKey = String(firmServiceType ?? "").trim();
  if (firmKey && firmKey !== "other" && firmKey !== "Other") {
    return firmServiceType;
  }
  return officeServiceType ?? firmServiceType;
}

export function formatAccountingServiceTypeLabel(
  serviceType?: AccountingOfficeDetail["serviceType"],
): string {
  const raw = String(serviceType ?? "").trim();
  if (!raw) {
    return "";
  }

  if (raw in OfficeServiceTypeLabels) {
    return OfficeServiceTypeLabels[raw as keyof typeof OfficeServiceTypeLabels];
  }
  if (raw in FirmServiceTypeLabels) {
    return FirmServiceTypeLabels[raw as keyof typeof FirmServiceTypeLabels];
  }
  return raw;
}

export function toOfficeServiceType(
  serviceType?: AccountingOfficeDetail["serviceType"],
): IOfficeNew["serviceType"] | undefined {
  if (!serviceType) {
    return undefined;
  }

  const raw = String(serviceType).trim();
  const officeKeys = new Set<IOfficeNew["serviceType"]>([
    "legal",
    "accounting",
    "investmentBanking",
    "other",
  ]);
  if (officeKeys.has(raw as IOfficeNew["serviceType"])) {
    return raw as IOfficeNew["serviceType"];
  }

  const firmToOffice: Record<string, IOfficeNew["serviceType"]> = {
    Legal: "legal",
    Accounting: "accounting",
    "Investment Banking": "investmentBanking",
    Other: "other",
    "Legal Services": "legal",
  };
  return firmToOffice[raw];
}

export function toOfficeUpdatePayload(
  office: AccountingOfficeDetail,
): IOfficeNew {
  const { serviceType, ...rest } = office;
  const normalizedServiceType = toOfficeServiceType(serviceType);
  return normalizedServiceType
    ? { ...rest, serviceType: normalizedServiceType }
    : { ...rest };
}

export function mergeOfficeAccountingFields(
  office: IOfficeNew,
  firm?: FirmAccountingSource | null,
): AccountingOfficeDetail {
  if (!office.id) {
    throw new Error("Office id is required");
  }

  return {
    ...office,
    id: office.id,
    shortId: office.shortId ?? firm?.shortId,
    dateJoined: office.dateJoined ?? firm?.dateJoined,
    dateWithdrawn: office.dateWithdrawn ?? firm?.dateWithdrawn,
    billingStartDate: office.billingStartDate ?? firm?.billingStartDate,
    membershipStatus: office.membershipStatus ?? firm?.membershipStatus,
    firmProfessionalsInPrimary:
      office.firmProfessionalsInPrimary ?? firm?.firmProfessionalsInPrimary,
    professionalsInPrimary:
      office.professionalsInPrimary ?? firm?.professionalsInPrimary,
    membershipType: (office.membershipType ?? firm?.membershipType) as
      | AccountingOfficeDetail["membershipType"]
      | undefined,
    serviceType: resolveAccountingServiceType(
      office.serviceType,
      firm?.serviceType,
    ),
    jurisdictionName: resolveFirmJurisdictionDisplay(office.jurisdiction, {
      jurisdiction: firm?.jurisdiction ?? office.jurisdiction,
      jurisdictionName: office.jurisdictionName,
      jurisdictionNames: firm?.jurisdictionNames,
    }),
  };
}

export function toFeeContext(office: AccountingOfficeDetail): {
  serviceType?: unknown;
  firmProfessionalsInPrimary?: string | number | null;
  professionalsInPrimary?: string | number | null;
  countOfPeople?: string | number | null;
  dateJoined?: Date | string | null;
} {
  return {
    serviceType: office.serviceType,
    firmProfessionalsInPrimary: office.firmProfessionalsInPrimary,
    professionalsInPrimary: office.professionalsInPrimary,
    countOfPeople: office.countOfPeople,
    dateJoined: office.dateJoined,
  };
}

async function getFirmContactForOfficeId(
  officeId: string,
  firmId?: string,
): Promise<FirmContactView | null> {
  const trimmedOfficeId = officeId.trim();
  if (!trimmedOfficeId) {
    return null;
  }

  const direct = await getFirmContactByOfficeId(trimmedOfficeId).catch(
    () => undefined,
  );
  if (direct) {
    return direct;
  }

  const trimmedFirmId = String(firmId ?? "").trim();
  if (!trimmedFirmId) {
    return null;
  }

  const firmContacts = await getFirmContactByFirmId(trimmedFirmId).catch(
    () => [],
  );
  return (
    firmContacts.find(
      (contact) => String(contact.officeId ?? "").trim() === trimmedOfficeId,
    ) ?? null
  );
}

/** Firm contact for accounting UI; secondary offices inherit the primary office contact. */
export async function getAccountingFirmContact(
  office: Pick<AccountingOfficeDetail, "id" | "firm" | "membershipType">,
): Promise<FirmContactView | null> {
  const officeId = String(office.id ?? "").trim();
  const firmId = String(office.firm ?? "").trim();
  if (!officeId) {
    return null;
  }

  const direct = await getFirmContactForOfficeId(officeId, firmId);
  if (direct) {
    return direct;
  }

  const isSecondary =
    String(office.membershipType ?? "").toLowerCase() === "secondary";
  if (!isSecondary || !firmId) {
    return null;
  }

  const siblings = await getOfficesByFirmId(firmId);
  const primaryOfficeId = buildAccountingFirmOfficeGroups(siblings).get(
    firmId,
  )?.parentOfficeId;
  if (!primaryOfficeId || primaryOfficeId === officeId) {
    return null;
  }

  return getFirmContactForOfficeId(primaryOfficeId, firmId);
}

export async function getAccountingOfficeDetail(
  officeId: string,
): Promise<AccountingOfficeDetail | null> {
  const trimmedId = officeId.trim();
  if (!trimmedId) {
    return null;
  }

  const office = await getOfficeById(trimmedId);
  if (!office?.id) {
    return null;
  }

  const firmId = String(office.firm ?? "").trim();
  const firm = firmId ? await getFirmById(firmId).catch(() => null) : null;

  return mergeOfficeAccountingFields(office, firm ?? undefined);
}

export function officeDisplayName(office: Pick<AccountingOfficeDetail, "name" | "firmName">): string {
  const name = String(office.name ?? "").trim();
  if (name) {
    return name;
  }
  return String(office.firmName ?? "").trim() || "Office";
}

/** Legal name printed on invoices, invoice PDFs, and invoice emails. */
export function officeLegalName(
  office: {
    firmLegalName?: string | null;
    firmName?: string | null;
    name?: string | null;
  },
  firm?: { firmName?: string | null } | null,
): string {
  const legal = String(office.firmLegalName ?? "").trim();
  if (legal) {
    return legal;
  }
  const firmLegal = String(firm?.firmName ?? "").trim();
  if (firmLegal) {
    return firmLegal;
  }
  const stored = String(office.firmName ?? "").trim();
  if (stored) {
    return stored;
  }
  return officeDisplayName({
    name: office.name ?? "",
    firmName: String(office.firmName ?? ""),
  });
}

export function officePartyKey(officeId: string): string {
  return `office:${officeId}`;
}

export type AccountingOfficeGroupTab = {
  id: string;
  label: string;
};

function formatAccountingOfficeGroupTabLabel(office: IOfficeNew): string {
  const jurisdictionLabel = String(
    office.jurisdictionName ??
      resolveFirmJurisdictionDisplay(office.jurisdiction, office) ??
      "",
  ).trim();

  if (office.membershipType && jurisdictionLabel) {
    const membershipLabel =
      MembershipType[office.membershipType as keyof typeof MembershipType] ??
      "";
    if (membershipLabel) {
      return `${membershipLabel} - ${jurisdictionLabel}`;
    }
  }

  if (jurisdictionLabel) {
    return jurisdictionLabel;
  }

  const blockHeading = formatOfficeBlockHeading(office).trim();
  if (blockHeading && blockHeading !== "-") {
    return blockHeading;
  }

  return officeDisplayName(office);
}

export async function getAccountingOfficeGroup(
  officeId: string,
): Promise<{ firmId: string; tabs: AccountingOfficeGroupTab[] }> {
  const trimmedId = officeId.trim();
  if (!trimmedId) {
    return { firmId: "", tabs: [] };
  }

  const office = await getOfficeById(trimmedId);
  if (!office?.id) {
    return { firmId: "", tabs: [] };
  }

  const firmId = String(office.firm ?? "").trim();
  if (!firmId) {
    return {
      firmId: "",
      tabs:
        office.showInDirectory === true
          ? [{ id: office.id, label: formatAccountingOfficeGroupTabLabel(office) }]
          : [],
    };
  }

  const siblings = await getOfficesByFirmId(firmId);
  const group = buildAccountingFirmOfficeGroups(siblings).get(firmId);

  if (!group || group.offices.length === 0) {
    return {
      firmId,
      tabs:
        office.showInDirectory === true
          ? [{ id: office.id, label: formatAccountingOfficeGroupTabLabel(office) }]
          : [],
    };
  }

  return {
    firmId,
    tabs: group.offices.map((item) => ({
      id: String(item.id),
      label: formatAccountingOfficeGroupTabLabel(item),
    })),
  };
}

/** Legacy payments.asp edit screen (financial.asp company parameters). */
export function buildLegacyPaymentsEditPath(
  office: Pick<AccountingOfficeDetail, "legacyId">,
  fiscalYear: number = getCurrentFiscalYear(),
): string | null {
  const legacyId = String(office.legacyId ?? "").trim();
  if (!legacyId) {
    return null;
  }

  const params = new URLSearchParams({
    action: "edit",
    f: "1",
    fy: String(fiscalYear),
    s: "jur",
    id: legacyId,
  });
  return `/payments.asp?${params.toString()}`;
}
